/**
 * @fileoverview Composable for chat message display and suggestions
 * @module composables/useChatMessages
 *
 * Manages message display, sorting, and autocomplete suggestions
 * for the chat window.
 *
 * @vue3migration - Phase 3B: Extracted from ChatWindow/Index.vue
 */
import { ref, computed, type ComputedRef } from 'vue'
import * as _ from 'lodash-es'
import { filterXSS } from 'xss'
import { parseLanguageBasedLabel } from '@/helpers/langLabels'

/**
 * Message interface for chat messages
 */
interface ChatMessage {
  id: string
  type: 'message' | 'reply' | 'postback' | 'agent'
  timestamp: Date | string
  data: {
    content: Array<{
      text?: string
      files?: any[]
      images?: any[]
      options?: Record<string, any>
    }>
    meta?: {
      name?: string
      lang?: string
      hideSuggestions?: boolean
    }
  }
  options?: Record<string, any>
}

/**
 * Suggestion item from fuzzy search
 */
interface SuggestionItem {
  item: {
    value: string
    language?: string
  }
  language?: string
}

/**
 * Options for useChatMessages composable
 */
interface UseChatMessagesOptions {
  /** Messages array from store */
  messages: ComputedRef<ChatMessage[]>
  /** Settings from store */
  settings: ComputedRef<any>
  /** Fuse.js search instance from store */
  fuse: ComputedRef<any>
}

/**
 * Composable for chat message display and suggestions
 *
 * @param options - Dependencies from store
 * @returns Message state and suggestion handlers
 *
 * @example
 * ```ts
 * const {
 *   sortedMessages,
 *   lastMessage,
 *   suggestions,
 *   chatbox,
 *   filteredXSSMessage,
 * } = useChatMessages({
 *   messages: computed(() => store.messages),
 *   settings: computed(() => store.settings),
 *   fuse: computed(() => store.fuse),
 * })
 * ```
 */
export function useChatMessages(options: UseChatMessagesOptions) {
  const { messages, settings, fuse } = options

  // ====================
  // State
  // ====================

  /** Current text in chat input */
  const chatbox = ref('')

  /** Autocomplete suggestions list */
  const suggestions = ref<SuggestionItem[]>([])

  /** Currently selected suggestion index (-1 = none) */
  const suggestSelect = ref(-1)

  /** Whether to show suggestions dropdown */
  const showSuggestion = ref(false)

  /** Last message sent by user (for iframe communication) */
  const lastUserMessage = ref('')

  // ====================
  // Computed - Message Display
  // ====================

  /**
   * Messages sorted by timestamp ascending
   */
  const sortedMessages = computed(() => {
    const messagesWithDates = messages.value.map(msg => ({
      ...msg,
      timestamp: new Date(msg.timestamp),
    }))
    return _.orderBy(messagesWithDates, ['timestamp'], ['asc'])
  })

  /**
   * Total number of messages
   */
  const messageSize = computed(() => sortedMessages.value.length)

  /**
   * First message in conversation
   */
  const firstMessage = computed(() => _.first(sortedMessages.value) || {} as ChatMessage)

  /**
   * Last message in conversation
   */
  const lastMessage = computed(() => _.last(sortedMessages.value) || {} as ChatMessage)

  /**
   * Last bot reply message with content
   */
  const lastBotMessage = computed(() => {
    const messagesWithContent = sortedMessages.value.filter(
      msg => !_.isEmpty(msg.data?.content)
    )
    return _.findLast(messagesWithContent, { type: 'reply' }) || {} as ChatMessage
  })

  /**
   * Whether last bot message has hideSuggestions flag
   */
  const hideSuggestion = computed(() =>
    _.get(lastBotMessage.value, 'data.meta.hideSuggestions', false)
  )

  // ====================
  // Computed - Input Processing
  // ====================

  /**
   * Chat input text filtered for XSS attacks
   */
  const filteredXSSMessage = computed(() => {
    const textBeforeXSSFilter = chatbox.value.trim()
    return filterXSS(textBeforeXSSFilter, {
      whiteList: {}, // empty, means filter out all tags
      stripIgnoreTag: true, // filter out all HTML not in the whitelist
      stripIgnoreTagBody: ['script'], // the script tag is a special case
    })
  })

  /**
   * Whether chatbox has valid length for autocomplete
   */
  const chatboxHasValidLength = computed(() => {
    const startAutocompleteCount = _.get(
      settings.value,
      'showSuggestions.toStartAutocomplete.startAutocompleteCount',
      3
    )
    const ignoreWhiteSpace = _.get(
      settings.value,
      'showSuggestions.toStartAutocomplete.ignoreWhiteSpace',
      false
    )
    const text = chatbox.value
    let chatboxLength = text.length
    if (ignoreWhiteSpace) {
      chatboxLength = text.replace(/\s/g, '').length
    }
    return text && chatboxLength >= startAutocompleteCount
  })

  // ====================
  // Computed - Labels
  // ====================

  /**
   * Placeholder text for typing state
   */
  const typingsLabel = computed(() => {
    const lang = _.get(lastBotMessage.value, 'data.meta.lang', 'default')
    return parseLanguageBasedLabel(
      settings.value.languageBasedLabels,
      'textareaTypingsPlaceholder',
      lang,
      'Type a message...'
    )
  })

  /**
   * Placeholder text when user should choose option
   */
  const chooseOptionLabel = computed(() => {
    const lang = _.get(lastBotMessage.value, 'data.meta.lang', 'default')
    return parseLanguageBasedLabel(
      settings.value.languageBasedLabels,
      'textareaChoicesPlaceholder',
      lang,
      'Choose an option above...'
    )
  })

  /**
   * Character limit countdown label
   */
  const countdownLimitLabel = computed(() => {
    const countdownType = settings.value.charLimit?.countdownType
    const limit = settings.value.charLimit?.limit || 1000
    return countdownType === 'increase'
      ? `${chatbox.value.length}/${limit}`
      : limit - chatbox.value.length
  })

  // ====================
  // Methods - Suggestions
  // ====================

  /**
   * Debounced suggestion listing based on input text
   * Updates suggestions array from fuzzy search
   */
  const debounceSuggestionListing = _.debounce((newText: string) => {
    if (settings.value.suggestions) {
      if (newText.length === 0) {
        const DEFAULT_SUGGESTIONS = _.filter(
          settings.value.suggestions,
          (suggestion: any) => suggestion.default === true
        ) || [{ value: 'What can I ask you?' }]
        suggestions.value = DEFAULT_SUGGESTIONS
      } else if (fuse.value) {
        const allSuggestions = fuse.value.search(newText)
        const suggestionsLimit = _.get(settings.value, 'showSuggestions.suggestionsLimit', 4)
        const topFewSuggestions = _.slice(allSuggestions, 0, suggestionsLimit)
        const singleLanguageResult = _.uniqBy(topFewSuggestions, 'language').length === 1
        suggestions.value = singleLanguageResult ? topFewSuggestions : []
      }
    }
  }, 500)

  /**
   * Toggle suggestion dropdown visibility
   *
   * @param toggle - Whether to show or hide
   * @param delay - Optional delay in ms
   */
  function toggleSuggestion(toggle: boolean, delay?: number) {
    const shouldShow = hideSuggestion.value ? false : toggle
    if (delay) {
      setTimeout(() => {
        showSuggestion.value = shouldShow
      }, delay)
    } else {
      showSuggestion.value = shouldShow
    }
  }

  /**
   * Change selected suggestion by delta
   *
   * @param change - Delta to apply to selection (-1 or 1)
   */
  function changeSuggestion(change: number) {
    const currentSelection = suggestSelect.value
    const targetSelection = currentSelection + change

    if (targetSelection < -1) {
      suggestSelect.value = suggestions.value.length - 1
    } else if (targetSelection > suggestions.value.length - 1) {
      suggestSelect.value = -1
    } else {
      suggestSelect.value = targetSelection
    }
  }

  /**
   * Set suggestion selection index directly
   *
   * @param index - Index to select
   */
  function setSuggestIndex(index: number) {
    suggestSelect.value = index
  }

  /**
   * Reset chatbox and suggestions after sending
   */
  function resetAfterSend() {
    chatbox.value = ''
    suggestions.value = []
    suggestSelect.value = -1
  }

  return {
    // State
    chatbox,
    suggestions,
    suggestSelect,
    showSuggestion,
    lastUserMessage,

    // Computed - Messages
    sortedMessages,
    messageSize,
    firstMessage,
    lastMessage,
    lastBotMessage,
    hideSuggestion,

    // Computed - Input
    filteredXSSMessage,
    chatboxHasValidLength,

    // Computed - Labels
    typingsLabel,
    chooseOptionLabel,
    countdownLimitLabel,

    // Methods
    debounceSuggestionListing,
    toggleSuggestion,
    changeSuggestion,
    setSuggestIndex,
    resetAfterSend,
  }
}
